import { Box, Button } from '@/components/ui';

import React, { forwardRef, ReactNode } from 'react';

import clsx from 'clsx';
import { IconX } from '@tabler/icons-react';

export type AlertVariant = 'solid' | 'soft' | 'surface' | 'outlined' | 'ghost';
export type AlertColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'feature';
export type AlertSize = 'xsmall' | 'small' | 'medium' | 'large';

interface BaseAlertProps {
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  variant?: AlertVariant;
  color?: AlertColor;
  size?: AlertSize;
  children?: ReactNode;
  className?: string;
  wrapperClassName?: string;
  onClose?: () => void;
  closeable?: boolean;
}

export type AlertProps = BaseAlertProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>;

const AlertRoot = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      icon,
      title,
      description,
      actions,
      variant = 'soft',
      color = 'primary',
      size = 'medium',
      className,
      wrapperClassName,
      onClose,
      closeable = false,
      children,
      ...props
    },
    ref
  ) => {
    const renderContent = () => (
      <>
        {icon && <div className="alert__icon">{icon}</div>}
        <div className="alert__content">
          {title && <div className="alert__title">{title}</div>}
          {description && (
            <div className="alert__description">{description}</div>
          )}
          {children}
        </div>
        {actions && <div className="alert__actions">{actions}</div>}
        {closeable && onClose && (
          <Button
            isIconOnly
            size="xsmall"
            variant="ghost"
            onClick={onClose}
            className="alert__close"
            aria-label="Close alert"
          >
            <IconX />
          </Button>
        )}
      </>
    );

    return (
      <Box
        ref={ref}
        className={clsx(
          'alert',
          `alert-size-${size}`,
          `alert-color-${color}`,
          `alert-variant-${variant}`,
          className
        )}
        role="alert"
        {...props}
      >
        <div className={clsx('alert__wrapper', wrapperClassName)}>
          {renderContent()}
        </div>
      </Box>
    );
  }
);

AlertRoot.displayName = 'AlertRoot';

// Icon component for backward compatibility
const AlertIcon = forwardRef<
  HTMLDivElement,
  { children: ReactNode; className?: string }
>(({ children, className }, ref) => {
  return (
    <div ref={ref} className={clsx('alert__icon', className)}>
      {children}
    </div>
  );
});

AlertIcon.displayName = 'AlertIcon';

// Close icon component
const AlertCloseIcon = forwardRef<
  HTMLButtonElement,
  { onClick?: () => void; className?: string }
>(({ onClick, className }, ref) => {
  return (
    <Button
      isIconOnly
      size="xsmall"
      variant="ghost"
      ref={ref}
      type="button"
      onClick={onClick}
      className={clsx('alert__close', className)}
      aria-label="Close alert"
    >
      <IconX />
    </Button>
  );
});

AlertCloseIcon.displayName = 'AlertCloseIcon';

// Main export
export const Alert = AlertRoot;

// Compound components for advanced usage
export const AlertCompound = Object.assign(AlertRoot, {
  Icon: AlertIcon,
  CloseIcon: AlertCloseIcon,
});

// Default export for backward compatibility
export default Alert;
